import { useState } from "react";
import { motion } from "framer-motion";
import { PHASES } from "@/lib/lunelle-data";
import { CycleRing } from "./CycleRing";
import { Reveal, MaskLines, Eyebrow } from "./Reveal";

export const Phases = () => {
  const [active, setActive] = useState("menstrual");
  const p = PHASES.find((x) => x.id === active);
  return (
    <section id="cycle" className="relative py-28 sm:py-40" data-testid="phases-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-end">
          <div>
            <Reveal><Eyebrow>01 — Your cycle, at the centre</Eyebrow></Reveal>
            <h2 className="mt-6 font-serif text-5xl font-light leading-[1] tracking-tight text-[#2C221E] sm:text-6xl lg:text-7xl">
              <MaskLines inView delay={0} lines={["Four seasons", <em key="e" className="italic text-[#9E4429]">live inside you.</em>]} />
            </h2>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-md text-base leading-relaxed text-[#5C4A42] sm:text-lg">
              Your cycle is the quiet rhythm behind everything in Lunelle. It follows flow, mood, energy, skin, sleep and
              cravings across all four phases, then shapes your food, style, money and rituals around them.
            </p>
          </Reveal>
        </div>

        <div className="mt-20 grid items-center gap-14 lg:grid-cols-[1fr_1.1fr]">
          <Reveal><CycleRing active={active} onSelect={setActive} /></Reveal>
          <div>
            <div className="flex flex-wrap gap-2" role="tablist">
              {PHASES.map((x) => (
                <button key={x.id} onClick={() => setActive(x.id)} data-testid={`phase-tab-${x.id}`}
                  className={`relative rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${active === x.id ? "text-[#FBF5EE]" : "text-[#5C4A42] hover:text-[#2C221E]"}`}>
                  {active === x.id && <motion.span layoutId="phase-pill" className="absolute inset-0 rounded-full bg-[#2C221E]" transition={{ type: "spring", stiffness: 300, damping: 30 }} />}
                  <span className="relative">{x.name}</span>
                </button>
              ))}
            </div>
            <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="mt-10 border-t border-[#C86D51]/20 pt-10" data-testid="phase-detail">
              <p className="font-serif text-2xl leading-snug text-[#2C221E] sm:text-3xl">{p.body}</p>
              <div className="mt-10 grid gap-8 sm:grid-cols-2">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A665C]">On your plate</span>
                  <ul className="mt-4 space-y-2.5">
                    {p.eat.map((e) => (
                      <li key={e} className="flex items-center gap-3 text-[#3D302B]">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.color }} />{e}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A665C]">Key nutrients</span>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {p.nutrients.map((n) => (
                      <span key={n} className="rounded-full border border-[#C86D51]/25 px-3.5 py-1.5 text-sm text-[#3D302B]">{n}</span>
                    ))}
                  </div>
                  <p className="mt-6 font-serif text-xl italic text-[#9E4429]">"{p.affirm}"</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
